import { useLazyQuery, useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  CredentialForStaffInput,
  CredentialInput,
  CredentialKind,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useState } from "react";
import { UPSERT_CREDENTIAL } from "routes/Customer/Credentials/schema";
import {
  CREDENTIAL_ID_FOR_PORTAL_USERNAME,
  UPSERT_CREDENTIAL_FOR_STAFF,
} from "routes/Staff/Credentials/CredentialManagement/api";
import { hasCriticalIssues } from "util/forms";
import { getGraphQLErrorCode } from "utils/errorUtils";

import { CredentialForm, CredentialValidator } from "./types";

// Matches `DUPLICATE_CREDENTIAL_ERROR_CODE` in `resolvers/upsertCredentialForStaff.ts`.
// A code, unlike a message, is the part of the error the server treats as a contract.
const DUPLICATE_CREDENTIAL_ERROR_CODE = "DUPLICATE_CREDENTIAL";

/** Fallback copy only; the server's own message is preferred when present. */
const DUPLICATE_MESSAGE = "A credential with this username already exists for this portal.";

function listLabels(labels: string[]): string {
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

/**
 * A half-filled form produces one issue per field — "Name is required", "Username is
 * required" — which read badly strung together. Rewrite a set of those as one sentence;
 * anything else passes through, since it carries information this can't summarise.
 */
export function friendlySaveError(message: string): string {
  if (!message) return "Something went wrong saving this credential.";

  const parts = message
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const labels = parts.map((part) => part.replace(/\s+is required\.?$/i, ""));
  const everyPartWasRequired = parts.every((part, index) => labels[index] !== part);

  if (!everyPartWasRequired || labels.length === 0) return message;
  return `Fill in ${listLabels(labels)} before saving.`;
}

function useCustomerWrite({
  form,
  credentialId,
  credentialTenantId,
  passwordForServer,
  onSaved,
}: {
  form: CredentialForm;
  credentialId?: string;
  credentialTenantId?: string | null;
  passwordForServer: (typed: string | null | undefined) => string;
  onSaved: () => void;
}) {
  const alerts = useAlerts();
  const { getValues } = form;
  const [upsert, { loading }] = useMutation(UPSERT_CREDENTIAL, {
    refetchQueries: ["Credentials"],
  });

  const write = useCallback(async () => {
    const input: CredentialInput = {
      ...getValues(),
      tenantId: credentialTenantId,
      // An untouched password sends "", which leaves the stored one alone.
      password: passwordForServer(getValues("password")),
      // Required by the CE schema, so a save can't get here without them; the form values
      // type them optional.
      kind: getValues("kind") ?? CredentialKind.Tierii,
      name: getValues("name") ?? "",
      username: getValues("username") ?? "",
    };

    try {
      await upsert({ variables: { id: credentialId, input } });
    } catch (error) {
      alerts.error(friendlySaveError(error instanceof Error ? error.message : ""));
      return false;
    }
    alerts.success("Credential saved successfully");
    onSaved();
    return true;
  }, [alerts, credentialId, credentialTenantId, getValues, onSaved, passwordForServer, upsert]);

  return { write, saving: loading };
}

/**
 * The unique `(portalId, username)` index spans every tenant, so the credential blocking a
 * save may be one the user can't otherwise find — look it up and offer to open it. Keyed on
 * the error code, not the message, which an English tweak would silently break.
 */
function useDuplicateCredentialLookup({
  form,
  credentialId,
}: {
  form: CredentialForm;
  credentialId?: string;
}) {
  const alerts = useAlerts();
  const { getValues } = form;
  const [duplicateExistingId, setDuplicateExistingId] = useState<string | null>(null);
  const [lookupExisting] = useLazyQuery(CREDENTIAL_ID_FOR_PORTAL_USERNAME, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    setDuplicateExistingId(null);
  }, [credentialId]);

  const reportSaveError = useCallback(
    async (error: unknown) => {
      const message = error instanceof Error ? error.message : "";
      if (getGraphQLErrorCode(error) !== DUPLICATE_CREDENTIAL_ERROR_CODE) {
        alerts.error(friendlySaveError(message));
        return;
      }

      alerts.error(message || DUPLICATE_MESSAGE);
      const portalId = getValues("portalId");
      const username = getValues("username");
      if (!portalId || !username) return;

      const { data: lookup } = await lookupExisting({ variables: { portalId, username } });
      setDuplicateExistingId(lookup?.credentialIdForPortalUsername ?? null);
    },
    [alerts, getValues, lookupExisting]
  );

  return { duplicateExistingId, reportSaveError };
}

function useStaffWrite({
  form,
  addingCredential,
  credentialId,
  passwordForServer,
  clearStaged,
  onSaved,
}: {
  form: CredentialForm;
  addingCredential: boolean;
  credentialId?: string;
  passwordForServer: (typed: string | null | undefined) => string;
  clearStaged: () => void;
  onSaved: () => void;
}) {
  const alerts = useAlerts();
  const { getValues } = form;
  const { duplicateExistingId, reportSaveError } = useDuplicateCredentialLookup({
    form,
    credentialId,
  });

  const [upsert, { loading: saving }] = useMutation(UPSERT_CREDENTIAL_FOR_STAFF, {
    refetchQueries: ["CredentialsForStaff", "CredentialForStaff"],
  });

  /** One call: the mutation writes the row and its links in a single transaction. */
  const write = useCallback(async () => {
    const values = { ...getValues(), password: passwordForServer(getValues("password")) };

    // Annotated: excess-property checking only fires on a literal assigned to a typed
    // target, and this is passed on as `variables: { input }`. Unannotated, a misspelled
    // key compiled clean and never reached the server.
    const input: CredentialForStaffInput = {
      name: values.name ?? "",
      username: values.username ?? "",
      kind: values.kind ?? CredentialKind.Tierii,
      email: values.email,
      portalId: values.portalId,
      isEncampOwned: values.isEncampOwned,
      // The full set the save leaves linked, staged grid changes included.
      facilityIds: values.facilityIds ?? [],
      password: values.password || undefined,
      securityQuestions: values.securityQuestions,
    };

    try {
      await upsert({ variables: { id: credentialId, input } });
    } catch (error) {
      await reportSaveError(error);
      return false;
    }

    clearStaged();
    alerts.success(addingCredential ? "Credential created" : "Credential saved");
    onSaved();
    return true;
  }, [
    addingCredential,
    alerts,
    clearStaged,
    credentialId,
    getValues,
    onSaved,
    passwordForServer,
    reportSaveError,
    upsert,
  ]);

  return { write, saving, duplicateExistingId };
}

export function useCredentialWrites({
  form,
  isCustomer,
  addingCredential,
  credentialId,
  credentialTenantId,
  passwordForServer,
  validateInput,
  clearStaged,
  onSaved,
}: {
  form: CredentialForm;
  isCustomer: boolean;
  addingCredential: boolean;
  credentialId?: string;
  credentialTenantId?: string | null;
  passwordForServer: (typed: string | null | undefined) => string;
  validateInput: CredentialValidator;
  clearStaged: () => void;
  onSaved: () => void;
}) {
  const alerts = useAlerts();
  const { getValues, trigger } = form;

  const customer = useCustomerWrite({
    form,
    credentialId,
    credentialTenantId,
    passwordForServer,
    onSaved,
  });
  const staff = useStaffWrite({
    form,
    addingCredential,
    credentialId,
    passwordForServer,
    clearStaged,
    onSaved,
  });

  /**
   * Awaited directly rather than read off `issues`, which is 500ms-debounced and a fast
   * click outruns. `trigger()` paints the outlines: `useValidatingForm` owns field errors,
   * so a direct `setError` is wiped by its next effect.
   */
  const validateForSubmit = useCallback(async () => {
    const values = { ...getValues(), password: passwordForServer(getValues("password")) };
    const submitIssues = await validateInput(values);
    if (!hasCriticalIssues(submitIssues)) return true;

    alerts.error(friendlySaveError(submitIssues.map((issue) => issue.message).join(",")));
    void trigger();
    return false;
  }, [alerts, getValues, passwordForServer, trigger, validateInput]);

  return {
    saving: isCustomer ? customer.saving : staff.saving,
    write: isCustomer ? customer.write : staff.write,
    duplicateExistingId: staff.duplicateExistingId,
    validateForSubmit,
  };
}
